import { useCallback, useMemo, useRef, useState } from 'react';
import {
    IconRotate2,
    IconRotate360,
    IconRotateClockwise2,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import { formatFileSize } from '../compress/compressPdfEngine';
import { rotatePdf } from './rotatePdfEngine';
import { getPdfPageCount } from '../split/splitPdfEngine';
import {
    applyPdfPageEdits,
    pagesHaveEdits,
    MAX_EDIT_PAGES_LARGE,
} from '../shared/pdfPageEditEngine';
import PdfDropZone from '../../components/pdf/PdfDropZone';
import { PdfUploadedFile } from '../../components/pdf/PdfFileRow';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import PdfDocPreview from '../../components/pdf/PdfDocPreview';
import PdfPageEditor from '../../components/pdf/PdfPageEditor';
import { usePdfPageEdits, fileKeyOf } from '../../components/pdf/usePdfPageEdits';
import Hint from '../../../../../components/common/Hint';
import ToolReadyHint from '../../components/common/ToolReadyHint';
import ToolCard from '../../components/common/ToolCard';
import {
    deriveToolStatus,
    downloadBlob,
    genericHowToSteps,
    getToolStatusText,
} from '../../toolPageUtils';
import { printBlob } from '../../../../../utils/generateDocPdf';
import { usePdfPaste } from '../../components/common/useFilePaste';

const MAX_FILE_BYTES = 500 * 1024 * 1024;

// Three distinct glyphs now that there is no label beside them: the old set drew
// counter-clockwise as a mirrored clockwise arrow and 180° as IconRotate2 — itself a
// counter-clockwise arrow — so two of the three looked the same.
const ROTATION_OPTIONS = [
    { id: 'cw90', delta: 90, labelKey: 'rotatePdf.rotations.cw90', Icon: IconRotateClockwise2 },
    { id: 'ccw90', delta: -90, labelKey: 'rotatePdf.rotations.ccw90', Icon: IconRotate2 },
    { id: 'flip180', delta: 180, labelKey: 'rotatePdf.rotations.flip180', Icon: IconRotate360 },
];

// Quick "rotate all pages" buttons for the visual editor.
const ROTATE_ALL = [
    { id: 'left', delta: 270, labelKey: 'rotatePdf.rotations.ccw90', Icon: IconRotate2 },
    { id: 'right', delta: 90, labelKey: 'rotatePdf.rotations.cw90', Icon: IconRotateClockwise2 },
    { id: 'flip', delta: 180, labelKey: 'rotatePdf.rotations.flip180', Icon: IconRotate360 },
];

/** Tile in an icon tray, same shape as the image tools' option trays. */
function iconTileClass(active = false) {
    return `flex min-h-[54px] items-center justify-center rounded-lg border-none px-1 py-1.5 transition-all ${
        active
            ? 'bg-white text-pdf-cat-solid shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:bg-zinc-900 dark:text-orange-400'
            : 'bg-transparent text-[#6B635C] hover:bg-white/60 hover:text-pdf-cat-solid dark:text-zinc-400 dark:hover:bg-zinc-900/60'
    }`;
}

export default function RotatePdf() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    const [file, setFile] = useState(null);
    const [pageCount, setPageCount] = useState(null);
    const [loadingMeta, setLoadingMeta] = useState(false);
    const [rotation, setRotation] = useState(null);
    const [scope, setScope] = useState(null);
    const [pageInput, setPageInput] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const clearResult = useCallback(() => {
        setResult(null);
        setError(null);
    }, []);

    const editFiles = useMemo(() => (file ? [file] : []), [file]);
    const { pagesByKey, updatePagesForKey, resetPageEdits } = usePdfPageEdits(editFiles, clearResult, MAX_EDIT_PAGES_LARGE);
    const pages = file ? pagesByKey[fileKeyOf(file)] : null;
    const editable = Array.isArray(pages);
    const hasEdits = editable && pagesHaveEdits(pages);

    const loadFile = useCallback(async (incoming) => {
        setError(null);
        if (!incoming) return;
        if (!(incoming.type === 'application/pdf' || incoming.name.toLowerCase().endsWith('.pdf'))) {
            const msg = t('rotatePdf.errors.invalidType');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }
        if (incoming.size > MAX_FILE_BYTES) {
            const msg = t('rotatePdf.errors.tooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        setLoadingMeta(true);
        setFile(incoming);
        setResult(null);
        setRotation(null);
        setScope(null);
        setPageInput('');
        try {
            const count = await getPdfPageCount(incoming);
            setPageCount(count);
        } catch (err) {
            setFile(null);
            setPageCount(null);
            const msg = err?.message === 'password'
                ? t('rotatePdf.errors.password')
                : t('rotatePdf.errors.failed');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setLoadingMeta(false);
        }
    }, [t]);

    // Ctrl/Cmd+V anywhere on the page drops a copied PDF straight in. This tool
    // holds one file, so a paste of several takes the first.
    usePdfPaste((files) => loadFile(files[0]));

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const dropped = e.dataTransfer.files?.[0];
        if (dropped) loadFile(dropped);
    };

    const clearFile = () => {
        setFile(null);
        setPageCount(null);
        setRotation(null);
        setScope(null);
        setPageInput('');
        setResult(null);
        setError(null);
        resetPageEdits();
    };

    const rotateAll = (delta) => {
        if (!file) return;
        updatePagesForKey(fileKeyOf(file), (prev) => prev.map((p) => (
            p.removed ? p : { ...p, rotation: ((((p.rotation || 0) + delta) % 360) + 360) % 360 }
        )));
    };

    const selectedRotation = ROTATION_OPTIONS.find((opt) => opt.id === rotation) || null;

    const handleRotate = async () => {
        if (!file || processing) return;

        recordToolUsage('pdf/rotate', { category: 'pdf' });

        setProcessing(true);
        setResult(null);
        setError(null);

        try {
            let blob;
            if (editable) {
                if (!pagesHaveEdits(pages)) throw new Error('empty');
                blob = await applyPdfPageEdits(file, pages);
            } else {
                blob = await rotatePdf(file, {
                    delta: selectedRotation?.delta || 0,
                    scope: scope || 'all',
                    pageInput,
                    pageCount,
                });
            }
            const base = file.name.replace(/\.pdf$/i, '');
            setResult({ name: `${base}-rotated.pdf`, blob });
            notifications.show({ title: t('common.success'), message: t('rotatePdf.done'), color: 'green' });
        } catch (err) {
            let msg = t('rotatePdf.errors.failed');
            if (err?.message === 'password') msg = t('rotatePdf.errors.password');
            else if (err?.message === 'empty') msg = t('rotatePdf.errors.emptySelection');
            else if (err?.message === 'removeAll') msg = t('rotatePdf.errors.emptySelection');
            else if (err?.message === 'invalid' || err?.message === 'outOfRange') {
                msg = t('rotatePdf.errors.invalidSelection', { total: toBnDigits(pageCount) });
            }
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
        }
    };

    const canRotate = file && pageCount && !loadingMeta && !processing && (
        editable
            ? hasEdits
            : (!!selectedRotation && !!scope && (scope === 'all' || !!pageInput.trim()))
    );

    const status = deriveToolStatus({
        hasInput: !!file,
        canRun: !!canRotate,
        processing,
        hasResult: !!result,
        hasError: !!error,
    });

    const howToSteps = useMemo(() => genericHowToSteps(t), [t]);
    const statusText = getToolStatusText(status, t, {
        success: t('rotatePdf.done'),
        error: error || t('toolPage.statusText.error'),
    });
    const fileMeta = file
        ? [
            formatFileSize(file.size),
            pageCount != null && !loadingMeta ? t('rotatePdf.pageCount', { count: toBnDigits(pageCount) }) : null,
            loadingMeta ? t('rotatePdf.reading') : null,
        ].filter(Boolean).join(' · ')
        : '';
    const resultMeta = result ? formatFileSize(result.blob.size) : undefined;

    return (
        <ToolPageLayout
            slug="pdf/rotate"
            subtitleKey="rotatePdf.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('toolPage.files', { count: toBnDigits(file ? 1 : 0) })}
            showAdd={!file && !loadingMeta}
            onAdd={() => fileInputRef.current?.click()}
            howToSteps={howToSteps}
            onReset={file || result || error ? clearFile : undefined}
            primaryAction={{
                label: t('rotatePdf.rotate'),
                onClick: handleRotate,
                disabled: !canRotate || !!result,
                loading: processing,
            }}
            controls={(
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/pdf,.pdf"
                        className="hidden"
                        onChange={(e) => { loadFile(e.target.files?.[0]); e.target.value = ''; }}
                    />

                    {!file ? (
                        <PdfDropZone
                            title={t('rotatePdf.dropTitle')}
                            hint={t('rotatePdf.dropHint')}
                            isDragging={isDragging}
                            onClick={() => fileInputRef.current?.click()}
                            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleDrop}
                        />
                    ) : (
                        <PdfUploadedFile
                            name={file.name}
                            meta={fileMeta}
                            onRemove={clearFile}
                            removeLabel={t('rotatePdf.removeFile')}
                        />
                    )}
                </>
            )}
            settings={file && pageCount != null && !loadingMeta ? (
                editable ? (
                    <ToolCard title={t('rotatePdf.rotationTitle')}>
                        <div className="space-y-3">
                            <p className="text-xs text-zinc-500 dark:text-zinc-400">{t('rotatePdf.rotateAllHint')}</p>
                            {/* Icon tray, like the image tools — each arrow shows its turn,
                                and the name is in the tooltip / aria-label. These are
                                one-shot actions, so no pressed state. */}
                            <div className="grid grid-cols-3 gap-1 rounded-xl bg-[#F7F5F3] p-1 dark:bg-zinc-800">
                                {ROTATE_ALL.map((opt) => (
                                    <Hint key={opt.id} label={t(opt.labelKey)}>
                                        <span className="flex min-w-0 [&>button]:w-full">
                                            <button
                                                type="button"
                                                onClick={() => rotateAll(opt.delta)}
                                                aria-label={t(opt.labelKey)}
                                                className={iconTileClass()}
                                            >
                                                <opt.Icon size={22} stroke={1.8} />
                                            </button>
                                        </span>
                                    </Hint>
                                ))}
                            </div>
                        </div>
                    </ToolCard>
                ) : (
                    <ToolCard title={t('rotatePdf.rotationTitle')}>
                        <div className="space-y-4">
                            {/* Icons only, three across — a full-width row per choice
                                would be an empty bar once the label is gone. The name
                                is in the tooltip / aria-label. */}
                            <div className="grid grid-cols-3 gap-2">
                                {ROTATION_OPTIONS.map((opt) => (
                                    <Hint key={opt.id} label={t(opt.labelKey)}>
                                        <span className="flex min-w-0 [&>button]:w-full">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setRotation((current) => (current === opt.id ? null : opt.id));
                                                setError(null);
                                                setResult(null);
                                            }}
                                            aria-label={t(opt.labelKey)}
                                            aria-pressed={rotation === opt.id}
                                            className={`flex items-center justify-center rounded-xl border px-3 py-3 transition-colors ${
                                                rotation === opt.id
                                                    ? 'border-primary bg-primary/10'
                                                    : 'border-zinc-200 dark:border-zinc-700'
                                            }`}
                                        >
                                            <opt.Icon
                                                size={22}
                                                className={`${opt.iconClass || ''} ${rotation === opt.id ? 'text-primary' : 'text-zinc-400'}`}
                                            />
                                        </button>
                                        </span>
                                    </Hint>
                                ))}
                            </div>
                            <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">{t('rotatePdf.scopeTitle')}</p>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    onClick={() => { setScope((current) => (current === 'all' ? null : 'all')); setError(null); setResult(null); }}
                                    className={`rounded-lg border px-3.5 py-2 text-[13px] font-semibold transition-colors ${
                                        scope === 'all'
                                            ? 'border-primary bg-primary/10 text-primary'
                                            : 'border-zinc-200 text-zinc-600 dark:border-zinc-700 dark:text-zinc-300'
                                    }`}
                                >
                                    {t('rotatePdf.scopeAll')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setScope((current) => (current === 'selected' ? null : 'selected')); setError(null); setResult(null); }}
                                    className={`rounded-lg border px-3.5 py-2 text-[13px] font-semibold transition-colors ${
                                        scope === 'selected'
                                            ? 'border-primary bg-primary/10 text-primary'
                                            : 'border-zinc-200 text-zinc-600 dark:border-zinc-700 dark:text-zinc-300'
                                    }`}
                                >
                                    {t('rotatePdf.scopeSelected')}
                                </button>
                            </div>

                            {scope === 'selected' && (
                                <div className="space-y-1.5">
                                    <label htmlFor="rotate-pages" className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                                        {t('rotatePdf.pagesLabel')}
                                    </label>
                                    <input
                                        id="rotate-pages"
                                        type="text"
                                        value={pageInput}
                                        onChange={(e) => { setPageInput(e.target.value); setError(null); setResult(null); }}
                                        placeholder={t('rotatePdf.pagesPlaceholder')}
                                        className="w-full rounded-xl border border-zinc-200 bg-white px-3.5 py-2.5 text-sm text-zinc-900 outline-none placeholder:text-zinc-400 focus:border-primary focus:ring-2 focus:ring-primary/30 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                                    />
                                    <p className="text-xs text-zinc-400">{t('rotatePdf.pagesHint', { total: toBnDigits(pageCount) })}</p>
                                </div>
                            )}
                        </div>
                    </ToolCard>
                )
            ) : null}
            preview={(
                <ToolPreviewPanel
                    title={t('toolPage.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('toolPage.previewEmpty')}
                    idleHint={t('toolPage.previewEmptyHint')}
                    processingLabel={t('rotatePdf.rotating')}
                    error={error}
                    successLabel={resultMeta}
                    downloadLabel={t('rotatePdf.download')}
                    onDownload={result ? () => downloadBlob(result.blob, result.name) : undefined}
                    onPrint={result ? () => printBlob(result.blob) : undefined}
                    onEditAgain={result ? clearResult : undefined}
                    editAgainLabel={t('toolPage.editAgain')}
                    onAgain={result ? clearFile : undefined}
                    againLabel={t('rotatePdf.startOver')}
                    readyHint={status === 'ready' ? <ToolReadyHint actionLabel={t('rotatePdf.rotate')} /> : null}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                    showIdle={!file && !error}
                    pdfPreview={!!file && !processing && !error}
                >
                    {file && !processing && !error ? (
                        result ? (
                            <PdfDocPreview source={result.blob} />
                        ) : (
                            <PdfPageEditor
                                files={editFiles}
                                pagesByKey={pagesByKey}
                                onPagesChangeForKey={updatePagesForKey}
                                activeIndex={0}
                                onActiveIndexChange={() => {}}
                            />
                        )
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
